// Demo mode only: adapts the illustrative mock service to the same view models as the live API.
import { getSeasonSummary, getHistoricalSeries, getForecast } from "@/services/orchardDataService";
import { STATIONS, STATIC_STATION_VMS } from "@/data/stations";
import { METRICS } from "@/config/metrics";

const slugOf = (stationNo) => STATIC_STATION_VMS.find((s) => s.id === stationNo)?.slug;
const toMockSel = (sel) => (sel.type === "station" ? { type: "station", stationId: slugOf(sel.stationId) } : sel);
const bySlug = (slug) => STATIONS.find((s) => s.id === slug);

const locationVM = (loc) => ({
  kind: loc.kind,
  id: loc.kind === "station" ? Number(bySlug(loc.id).cimisId) : "custom",
  name: loc.name,
  lat: loc.lat,
  lng: loc.lng,
  elevationFt: null,
  nearestStation: { id: Number(loc.nearest.station.cimisId), name: loc.nearest.station.name },
  nearestKm: loc.nearest.distanceMi * 1.60934,
  roughEstimate: loc.confidence.isFar,
  weights: loc.weights.map((w) => ({ id: Number(bySlug(w.id).cimisId), name: bySlug(w.id).name, weight: w.weight })),
});

const metricVM = (m, c, asOf, season) => ({
  metric: m,
  units: METRICS[m].unit,
  issueDate: asOf,
  season: season ?? c.latestSeason,
  seasonStart: null,
  seasonToDate: c.value,
  daysMissing: null,
  next7: null,
  normalNext7: null,
  projected: null,
  projectedThrough: null,
  pastSeasons: [],
  pastAvg: c.avg,
  pastMin: c.min,
  pastMax: c.max,
  pastLabel: `${c.priorSeasons[0]} – ${c.priorSeasons[3]}`,
  pctVsPast: (c.value / c.avg - 1) * 100,
});

export async function demoSummary(sel) {
  const s = await getSeasonSummary(toMockSel(sel));
  return {
    location: locationVM(s.location),
    metrics: {
      chillHours: metricVM("chillHours", s.chillHours, s.asOf),
      chillPortions: metricVM("chillPortions", s.chillPortions, s.asOf),
      gdd: metricVM("gdd", s.gdd, s.asOf, s.gdd.season),
    },
    daily: null,
  };
}

export async function demoSeasons(sel, metric) {
  const h = await getHistoricalSeries(toMockSel(sel), metric);
  return {
    metric,
    units: METRICS[metric].unit,
    issueLabel: h.todayLabel,
    currentLabel: h.currentLabel,
    priorLabels: h.priorLabels,
    points: h.points.map((p) => ({ ...p, currentObserved: p.current, currentForecast: null })),
    note: `${h.note} All values are illustrative mock data.`,
  };
}

export async function demoForecast(sel, horizon) {
  const f = await getForecast(toMockSel(sel), horizon);
  const metrics = Object.fromEntries(
    Object.entries(f.metrics).map(([m, d]) => {
      const fc = d.points.filter((p) => p.lead);
      return [
        m,
        {
          current: d.current,
          projected: d.projected,
          added: d.added,
          low: d.low,
          high: d.high,
          normalNext7: null,
          days: fc.map((p, i) => ({ date: p.date, label: p.label, horizon: p.lead, value: p.forecast - (i ? fc[i - 1].forecast : d.current) })),
          points: d.points,
        },
      ];
    }),
  );
  return { metrics, issueLabel: f.todayLabel, hasBand: true, horizon };
}
